import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { etatDesLieuxApi } from '../services/api';

const EtatDesLieuxDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [etat, setEtat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEtatDesLieux();
  }, [id]);

  const fetchEtatDesLieux = async () => {
    try {
      setLoading(true);
      const response = await etatDesLieuxApi.getById(id);
      const etatData = response.data;

      // Parse JSON fields
      etatData.meterReadingsParsed = etatData.meterReadings ? JSON.parse(etatData.meterReadings) : null;
      etatData.keysParsed = etatData.keys ? JSON.parse(etatData.keys) : [];
      etatData.privateAreasParsed = etatData.privateAreas ? JSON.parse(etatData.privateAreas) : null;
      etatData.otherEquipmentParsed = etatData.otherEquipment ? JSON.parse(etatData.otherEquipment) : null;
      etatData.roomsParsed = etatData.rooms ? JSON.parse(etatData.rooms) : [];

      setEtat(etatData);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'état des lieux');
      navigate('/etat-des-lieux');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      await etatDesLieuxApi.download(id);
      toast.success('Téléchargement en cours...');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet état des lieux ?')) {
      return;
    }

    try {
      await etatDesLieuxApi.delete(id);
      toast.success('État des lieux supprimé avec succès');
      navigate('/etat-des-lieux');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!etat) {
    return null;
  }

  const isEntree = etat.type === 'entrée';
  const canEdit = etat.status === 'draft' && !etat.landlordSigned && !etat.tenantSigned;
  const canSign = etat.status !== 'signed';
  const canDelete = etat.status === 'draft' && !etat.landlordSigned && !etat.tenantSigned;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              État des lieux - {etat.type === 'entrée' ? 'Entrée' : 'Sortie'}
            </h1>
            <p className="text-gray-600 mt-1">
              {etat.property?.name} - {etat.tenant?.firstName} {etat.tenant?.lastName}
            </p>
          </div>

          <div className="flex space-x-2">
            <Link
              to="/etat-des-lieux"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Retour
            </Link>

            {canEdit && (
              <Link
                to={`/etat-des-lieux/${id}/edit`}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Modifier
              </Link>
            )}

            {canSign && (
              <Link
                to={`/etat-des-lieux/${id}/sign`}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Signer
              </Link>
            )}

            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              Télécharger PDF
            </button>

            {canDelete && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            )}
          </div>
        </div>

        {/* Status badges */}
        <div className="flex space-x-2">
          <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
            etat.type === 'entrée' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
          }`}>
            {etat.type === 'entrée' ? 'Entrée' : 'Sortie'}
          </span>

          <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
            etat.status === 'signed' ? 'bg-green-100 text-green-800' :
            etat.status === 'pending_signature' ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {etat.status === 'signed' ? 'Signé' :
             etat.status === 'pending_signature' ? 'En attente de signature' :
             'Brouillon'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Informations générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Date d'inspection</p>
              <p className="mt-1 text-gray-900">{new Date(etat.inspectionDate).toLocaleDateString('fr-FR')}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Adresse du bien</p>
              <p className="mt-1 text-gray-900">{etat.propertyAddress}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Bailleur</p>
              <p className="mt-1 text-gray-900">{etat.landlordName}</p>
              <p className="text-sm text-gray-500">{etat.landlordAddress}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Locataire</p>
              <p className="mt-1 text-gray-900">{etat.tenantName}</p>
              <p className="text-sm text-gray-500">{etat.tenantAddress}</p>
            </div>
            {etat.previousOccupantName && (
              <div>
                <p className="text-sm font-medium text-gray-500">Ancien occupant</p>
                <p className="mt-1 text-gray-900">{etat.previousOccupantName}</p>
              </div>
            )}
          </div>
        </div>

        {/* Meter Readings */}
        {etat.meterReadingsParsed && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Relevés des compteurs</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Électricité</p>
                <p className="text-sm text-gray-600">
                  N° {etat.meterReadingsParsed.electricityNumber} |
                  HP: {etat.meterReadingsParsed.electricityHPleines} |
                  HC: {etat.meterReadingsParsed.electricityHCreuses}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Gaz naturel</p>
                <p className="text-sm text-gray-600">
                  N° {etat.meterReadingsParsed.gasNumber} |
                  Relevé: {etat.meterReadingsParsed.gasReading}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Eau</p>
                <p className="text-sm text-gray-600">
                  Chaude: {etat.meterReadingsParsed.waterHot} m³ |
                  Froide: {etat.meterReadingsParsed.waterCold} m³
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Energy Equipment */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Équipements énergétiques</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Chauffage</p>
              <p className="mt-1 text-gray-900">{etat.heatingType} {etat.heatingTypeOther}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Eau chaude</p>
              <p className="mt-1 text-gray-900">{etat.waterHeatingType} {etat.waterHeatingTypeOther}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">État chaudière</p>
              <p className="mt-1 text-gray-900">{etat.boilerState || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Dernier entretien</p>
              <p className="mt-1 text-gray-900">
                {etat.boilerLastMaintenance ? new Date(etat.boilerLastMaintenance).toLocaleDateString('fr-FR') : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">État ballon eau chaude</p>
              <p className="mt-1 text-gray-900">{etat.waterHeaterState || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Keys */}
        {etat.keysParsed && etat.keysParsed.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Inventaire des clés</h2>
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-sm font-medium text-gray-500">Type</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-500">Nombre</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-500">Commentaires</th>
                </tr>
              </thead>
              <tbody>
                {etat.keysParsed.map((key, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-2 text-sm">{key.type}</td>
                    <td className="py-2 text-sm">{key.number}</td>
                    <td className="py-2 text-sm">{key.comments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Rooms */}
        {etat.roomsParsed && etat.roomsParsed.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Inspection des pièces</h2>
            <div className="space-y-6">
              {etat.roomsParsed.map((room, roomIndex) => (
                <div key={roomIndex} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-lg text-gray-800 mb-3">{room.roomName}</h3>
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">Élément</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">État d'entrée</th>
                        {!isEntree && (
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">État de sortie</th>
                        )}
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">Commentaires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {room.elements.map((element, elemIndex) => (
                        <tr key={elemIndex} className="border-b">
                          <td className="py-2 px-3 text-sm">{element.name}</td>
                          <td className="py-2 px-3 text-sm">{element.entryState || '-'}</td>
                          {!isEntree && (
                            <td className="py-2 px-3 text-sm">{element.exitState || '-'}</td>
                          )}
                          <td className="py-2 px-3 text-sm">{element.comments || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General Comments */}
        {etat.generalComments && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Commentaires généraux</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{etat.generalComments}</p>
          </div>
        )}

        {/* Signatures */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Signatures</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Bailleur</h3>
              {etat.landlordSigned ? (
                <div>
                  <p className="text-sm text-green-600 mb-2">
                    ✓ Signé le {new Date(etat.landlordSignedAt).toLocaleDateString('fr-FR')}
                  </p>
                  {etat.landlordSignature && (
                    <img
                      src={etat.landlordSignature}
                      alt="Signature bailleur"
                      className="border border-gray-300 rounded"
                      style={{ maxWidth: '200px', maxHeight: '100px' }}
                    />
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Non signé</p>
              )}
            </div>

            <div>
              <h3 className="font-medium text-gray-700 mb-2">Locataire</h3>
              {etat.tenantSigned ? (
                <div>
                  <p className="text-sm text-green-600 mb-2">
                    ✓ Signé le {new Date(etat.tenantSignedAt).toLocaleDateString('fr-FR')}
                  </p>
                  {etat.tenantSignature && (
                    <img
                      src={etat.tenantSignature}
                      alt="Signature locataire"
                      className="border border-gray-300 rounded"
                      style={{ maxWidth: '200px', maxHeight: '100px' }}
                    />
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Non signé</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EtatDesLieuxDetail;
